import { ConnectionFields } from './ConnectionFields';
import { MailboxesPanel } from './Mailboxes';
import { ProgramMappings } from './ProgramMappings';
import { ExplorationPanel, WEB_BLOCKED, WebConnectForm, webOffered, WebWrites } from './WebConnect';
import type { ConnectionPreview, SoftwareDto } from '@bk/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { api, errorMessage } from '../../lib/api';
import { dateTime } from '../../lib/format';
import { navigate } from '../../lib/router';
import { useTenant, useTenantQuery } from '../../lib/useTenant';
import { Badge, Button, Card, Empty, ErrorText, Field, PageTitle, SectionLoader, Select } from '../../ui';
import { SourceProgress } from './WorkProgress';

const FEASIBILITY: Record<string, { label: string; tone: 'green' | 'amber' | 'gray' }> = {
  collegabile: { label: 'collegabile', tone: 'green' },
  dal_sito: { label: 'collegabile dal sito', tone: 'green' },
  solo_export: { label: 'solo export di file', tone: 'amber' },
  manuale: { label: 'solo uso manuale', tone: 'gray' },
};
const SOURCE: Record<string, string> = {
  conversazione: 'dalla conversazione',
  upload: 'da un file caricato',
  dichiarato: 'dichiarato',
};

/** Collegamento: credenziali in un modulo sicuro, prova con anteprima dei dati, poi conferma. */
export function ConnectForm({ s, onDone }: { s: SoftwareDto; onDone: () => void }) {
  const { t } = useTenant();
  const fields = s.assessment?.connection_fields ?? [];
  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(fields.filter((f) => f.default).map((f) => [f.key, f.default!])),
  );
  const [preview, setPreview] = useState<ConnectionPreview | null>(null);
  const test = useMutation({
    mutationFn: () =>
      api<ConnectionPreview>(t(`/software/${s.id}/connection/preview`), {
        body: { credentials: values },
      }),
    onSuccess: setPreview,
  });
  const connect = useMutation({
    mutationFn: (onlyWorking: boolean) =>
      api<SoftwareDto>(t(`/software/${s.id}/connection`), {
        body: { credentials: values, ...(onlyWorking ? { only_working: true } : {}) },
      }),
    onSuccess: onDone,
  });
  // Accesso riuscito ma qualche dato non si legge: si può collegare intanto ciò che funziona.
  const partial =
    preview && !preview.ok && preview.test === 'collegamento riuscito'
      ? preview.resources.filter((r) => !r.error)
      : null;
  const oauth = !!s.assessment?.oauth;
  // OAuth2: il browser va alla pagina di consenso del servizio e torna qui con l'esito.
  const authorize = useMutation({
    mutationFn: () =>
      api<{ url: string }>(t(`/software/${s.id}/connection/oauth`), {
        body: { credentials: values },
      }),
    onSuccess: (r) => window.location.assign(r.url),
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (oauth) authorize.mutate();
    else test.mutate();
  };
  return (
    <form className="mt-3 space-y-3 rounded-lg bg-black/[0.03] p-3" onSubmit={submit}>
      <p className="text-xs text-ink/60">
        {oauth
          ? 'I dati dell’applicazione e il permesso concesso restano cifrati e non passano mai dalla chat'
          : 'Le credenziali restano cifrate e non passano mai dalla chat'}
        {s.assessment?.connection_hosts.length ? (
          <>
            ; vengono inviati solo a <b>{s.assessment.connection_hosts.join(' e ')}</b>
          </>
        ) : null}
        .{' '}
        {oauth
          ? 'Dopo l’autorizzazione il sistema prova a leggere i dati e collega solo se la prova riesce.'
          : 'Prima si prova e si guarda un’anteprima; si collega solo se i dati sono quelli giusti.'}
      </p>
      {fields.map((f) => (
        <div key={f.key}>
          {f.options?.length ? (
            // Si sceglie per posizione: un'opzione può avere valore vuoto (es. produzione senza prefisso
            // nell'indirizzo) e non va confusa con "nessuna scelta".
            <Select
              label={f.label}
              value={
                f.key in values ? String(f.options.findIndex((o) => o.value === values[f.key])) : ''
              }
              onChange={(e) => {
                const o = f.options![Number(e.target.value)];
                const next = { ...values };
                if (o) next[f.key] = o.value;
                else delete next[f.key];
                setValues(next);
              }}
              required
            >
              <option value="" />
              {f.options.map((o, i) => (
                <option key={i} value={String(i)}>
                  {o.label}
                </option>
              ))}
            </Select>
          ) : (
            <Field
              label={f.label}
              type={f.secret ? 'password' : 'text'}
              autoComplete="off"
              value={values[f.key] ?? ''}
              onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
              required
            />
          )}
          {f.help && <p className="mt-1 text-xs text-ink/50">{f.help}</p>}
        </div>
      ))}
      {oauth && s.oauth_redirect_uri && (
        <div className="rounded-lg border border-black/10 bg-white p-2 text-xs text-ink/70">
          <p>
            {s.name} si collega con l’accesso dal browser: serve un’applicazione registrata presso{' '}
            {s.name} (vedi le indicazioni dei campi). Nell’applicazione registra questo indirizzo di
            ritorno:
          </p>
          <div className="mt-1 flex items-center gap-2">
            <code className="min-w-0 flex-1 truncate">{s.oauth_redirect_uri}</code>
            <button
              type="button"
              className="text-accent hover:underline"
              onClick={() => void navigator.clipboard?.writeText(s.oauth_redirect_uri!)}
            >
              copia
            </button>
          </div>
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        {oauth ? (
          <Button type="submit" disabled={authorize.isPending}>
            {authorize.isPending ? 'Apertura…' : `Accedi a ${s.name} e autorizza`}
          </Button>
        ) : (
          <Button type="submit" variant="ghost" disabled={test.isPending}>
            {test.isPending
              ? 'Prova in corso (se qualcosa non torna il sistema corregge e riprova)…'
              : 'Prova e mostra anteprima'}
          </Button>
        )}
        {preview?.ok && (
          <Button type="button" disabled={connect.isPending} onClick={() => connect.mutate(false)}>
            {connect.isPending ? 'Collegamento…' : 'I dati sono giusti: collega'}
          </Button>
        )}
        {partial && partial.length > 0 && (
          <Button type="button" disabled={connect.isPending} onClick={() => connect.mutate(true)}>
            {connect.isPending
              ? 'Collegamento…'
              : `Collega solo ${partial.map((r) => r.label).join(', ')}`}
          </Button>
        )}
      </div>
      {preview?.repaired && (
        <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-900">
          La prima prova non tornava: il sistema ha ricontrollato la documentazione, ha corretto il
          collegamento e ha riprovato. {preview.repaired}
        </p>
      )}
      {preview && !preview.ok && (
        <p className="text-sm text-red-700">
          {preview.test !== 'collegamento riuscito'
            ? preview.test
            : preview.resources.find((r) => r.error)?.error}
        </p>
      )}
      {preview?.resources.map((r) => (
        <div key={r.key} className="text-xs">
          <p className="font-medium">
            {r.label}:{' '}
            {r.error ? (
              <span className="text-red-700">{r.error}</span>
            ) : (
              `${r.rows} righe di esempio`
            )}
          </p>
          {r.sample.length > 0 && (
            <div className="mt-1 overflow-x-auto">
              <table className="text-left">
                <thead>
                  <tr>
                    {Object.keys(r.sample[0]!).map((k) => (
                      <th key={k} className="pr-3 font-medium text-ink/60">
                        {k}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {r.sample.map((row, i) => (
                    <tr key={i}>
                      {Object.values(row).map((v, j) => (
                        <td key={j} className="pr-3">
                          {String(v ?? '')}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ))}
      <ErrorText>
        {test.error
          ? errorMessage(test.error)
          : connect.error
            ? errorMessage(connect.error)
            : authorize.error
              ? errorMessage(authorize.error)
              : ''}
      </ErrorText>
    </form>
  );
}

/**
 * Riquadro di un software. Con `operations` (nella scheda, ADR 0121) solo le operazioni: nome, usi, specifiche, ultima
 * lettura ed errori stanno già nei riquadri della scheda.
 */
export function SoftwareCard({ s, operations = false }: { s: SoftwareDto; operations?: boolean }) {
  const { tenant, t, isManager } = useTenant();
  const queryClient = useQueryClient();
  const [connecting, setConnecting] = useState(false);
  const [webConnecting, setWebConnecting] = useState(false);
  const refresh = () =>
    void queryClient.invalidateQueries({ queryKey: ['tenant', tenant.id, 'software'] });
  const [hint, setHint] = useState('');
  const [hinting, setHinting] = useState(false);
  const [product, setProduct] = useState('');
  const assess = useMutation({
    mutationFn: () =>
      api<SoftwareDto>(t(`/software/${s.id}/assess`), {
        body: {
          ...(hint.trim() ? { hint: hint.trim() } : {}),
          ...(product.trim() ? { product: product.trim() } : {}),
        },
      }),
    onSuccess: () => {
      setHinting(false);
      setHint('');
      setProduct('');
      refresh();
    },
  });
  const sync = useMutation({
    mutationFn: () => api<SoftwareDto>(t(`/software/${s.id}/connection/sync`), { method: 'POST' }),
    onSuccess: refresh,
  });
  const remove = useMutation({
    mutationFn: () => api<void>(t(`/software/${s.id}/remove`), { method: 'POST' }),
    onSuccess: refresh,
  });
  const remap = useMutation({
    mutationFn: () => api<SoftwareDto>(t(`/software/${s.id}/connection/web/remap`), { method: 'POST' }),
    onSuccess: refresh,
  });
  const disconnect = useMutation({
    mutationFn: () => api<void>(t(`/software/${s.id}/connection/remove`), { method: 'POST' }),
    onSuccess: refresh,
  });
  const a = s.assessment;
  const f = a ? FEASIBILITY[a.feasibility] : null;
  return (
    <Card>
      {!operations && (
        <>
          <div className="flex flex-wrap items-start gap-2">
            <div className="min-w-0 flex-1">
              <p className="font-semibold">
                {s.name}
                {s.product && s.product !== s.name && (
                  <span className="font-normal text-ink/55"> · {s.product}</span>
                )}
              </p>
              <p className="text-xs text-ink/50">
                {a?.category ?? s.category ?? 'categoria da capire'}
                {s.uploads_30d > 0 && ` · ${s.uploads_30d} file esportati negli ultimi 30 giorni`}
              </p>
            </div>
            {s.connection ? (
              <Badge tone={s.connection.status === 'attivo' ? 'green' : s.connection.status === 'sospeso' ? 'amber' : 'red'}>
                {s.connection.status === 'attivo'
                  ? s.connection.kind === 'api'
                    ? 'collegato'
                    : 'collegato dal sito'
                  : s.connection.status === 'sospeso'
                    ? 'collegamento sospeso'
                    : 'collegamento in errore'}
              </Badge>
            ) : (
              f && <Badge tone={f.tone}>{f.label}</Badge>
            )}
            {a && !s.connection && (
              <Badge>{a.verified ? 'verificato' : `da verificare · confidenza ${a.confidence}`}</Badge>
            )}
          </div>
          <ul className="mt-2 space-y-1 text-sm text-ink/75">
            {s.usages.slice(0, 4).map((u) => (
              <li key={u.id}>
                {u.description ?? u.how}
                <span className="text-xs text-ink/45">
                  {' '}
                  · {SOURCE[u.source] ?? u.source}
                  {u.process_key && ` · processo ${u.process_key.replace(/_/g, ' ')}`}
                </span>
              </li>
            ))}
          </ul>
        </>
      )}
      {(s.assessing || assess.isPending) && (
        <p className="mt-2 text-sm text-ink/55">
          Verifica in corso: il sistema legge la documentazione ufficiale per capire se e come si
          collega (può volerci un minuto)…
        </p>
      )}
      {!a && !s.assessing && !assess.isPending && s.product_unrecognized && (
        <div className="mt-2 text-sm text-ink/70">
          <p>
            Dal nome non si capisce quale programma sia. Indica il nome del prodotto (es. Google
            Drive, Fatture in Cloud): il sistema verifica se e come si collega.
          </p>
          {isManager && (
            <form
              className="mt-2 flex flex-wrap items-end gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                if (product.trim()) assess.mutate();
              }}
            >
              <div className="min-w-48 flex-1">
                <Field
                  label="Nome del prodotto"
                  value={product}
                  onChange={(e) => setProduct(e.target.value)}
                />
              </div>
              <Button type="submit" variant="ghost" disabled={!product.trim()}>
                Verifica
              </Button>
            </form>
          )}
        </div>
      )}
      {!a && !s.assessing && !s.product_unrecognized && (
        <p className="mt-2 text-sm text-ink/55">
          Non ancora verificato.
          {!assess.isPending && isManager && (
            <button className="ml-2 text-accent hover:underline" onClick={() => assess.mutate()}>
              Verifica ora
            </button>
          )}
        </p>
      )}
      {a && (
        <div className="mt-2 space-y-1 text-sm text-ink/70">
          {!operations && (
            <>
              {a.readable.length > 0 && <p>Si possono leggere: {a.readable.join(', ')}.</p>}
              {a.has_official_mcp && <p>Ha un server MCP ufficiale.</p>}
              {a.notes && <p className="text-xs text-ink/55">{a.notes}</p>}
              <p className="text-xs text-ink/55">
                {a.doc_source === 'specifica_api' ? (
                  <>
                    Ricavato dalla{' '}
                    <a
                      className="text-accent hover:underline"
                      href={a.spec_url ?? '#'}
                      target="_blank"
                      rel="noreferrer"
                    >
                      specifica ufficiale delle API
                    </a>
                    .
                  </>
                ) : a.doc_source === 'documentazione' ? (
                  'Ricavato dalla documentazione ufficiale consultata.'
                ) : (
                  <span className="text-amber-800">
                    Non è stato possibile consultare la documentazione: valutazione dalla conoscenza
                    generale, da verificare con attenzione.
                  </span>
                )}
              </p>
              {a.sources.length > 0 && (
                <details className="text-xs text-ink/55">
                  <summary className="cursor-pointer">Pagine consultate ({a.sources.length})</summary>
                  <ul className="mt-1 space-y-0.5">
                    {a.sources.slice(0, 10).map((u) => (
                      <li key={u} className="truncate">
                        <a
                          className="text-accent hover:underline"
                          href={u}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {u}
                        </a>
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </>
          )}
          {!a.verified && !s.connection && isManager && !hinting && !s.assessing && (
            <button
              className="mr-3 text-xs text-accent hover:underline"
              onClick={() => setHinting(true)}
            >
              I dati richiesti non tornano? Correggi e verifica di nuovo
            </button>
          )}
          {hinting && (
            <form
              className="mt-2 space-y-2 rounded-lg bg-black/[0.03] p-3"
              onSubmit={(e) => {
                e.preventDefault();
                assess.mutate();
              }}
            >
              <Field
                label="Cosa sai di come ci si collega (facoltativo)"
                placeholder="es. le API funzionano con username e password dell’account"
                value={hint}
                onChange={(e) => setHint(e.target.value)}
              />
              <p className="text-xs text-ink/50">
                Non scrivere qui password o chiavi: serve solo a descrivere il modo di accesso.
              </p>
              <div className="flex gap-2">
                <Button type="submit" variant="ghost" disabled={assess.isPending}>
                  {assess.isPending ? 'Verifica in corso…' : 'Verifica di nuovo'}
                </Button>
                <Button type="button" variant="ghost" onClick={() => setHinting(false)}>
                  Annulla
                </Button>
              </div>
            </form>
          )}
          {!operations && a.api_docs_url && (
            <a
              className="text-xs text-accent hover:underline"
              href={a.api_docs_url}
              target="_blank"
              rel="noreferrer"
            >
              Documentazione delle API
            </a>
          )}
          {!operations && a.feasibility === 'collegabile' && !a.has_template && !s.connection && !s.assessing && (
            <p className="text-xs text-amber-800">
              Ha delle API, ma dalla documentazione il sistema non è riuscito a ricavare un modo
              sicuro di collegarsi (il motivo è nelle note qui sopra): per ora si lavora a mano o
              con l’export. Puoi riprovare con “Correggi e verifica di nuovo”.
            </p>
          )}
          {!operations && !s.connection && !a.has_template && WEB_BLOCKED[a.web.status] && (
            <p className="text-xs text-amber-800">{WEB_BLOCKED[a.web.status]}</p>
          )}
          {a.feasibility === 'solo_export' && (
            <p className="text-xs text-ink/55">
              Si lavora con gli export: caricali dall’Archivio, il sistema impara a leggerli.
            </p>
          )}
        </div>
      )}
      {s.connection && (
        <div className="mt-3 text-sm">
          {operations ? null : s.connection.syncing ? (
            s.connection.progress ? (
              // Coda di lavorazione (ADR 0043): righe lette man mano, file scaricati in lettura.
              <SourceProgress source={s.connection.progress} title="Sincronizzazione in corso" />
            ) : (
              <p className="text-ink/65">
                Sincronizzazione in corso: il sistema legge tutte le pagine fino all’ultima (con molti
                dati può volerci qualche minuto)…
              </p>
            )
          ) : (
            <p className="text-ink/65">
              Ultima sincronizzazione: {dateTime(s.connection.last_sync_at)}
              {s.connection.last_result != null &&
                ` · ${Object.entries(
                  s.connection.last_result as Record<string, { righe: number; incompleto?: boolean; allegati?: { scaricati: number; da_vedere: number } }>,
                )
                  .map(
                    ([k, v]) =>
                      `${v.righe} ${k}${v.incompleto ? ' (fermata al limite di sicurezza)' : ''}${
                        v.allegati
                          ? ` (${v.allegati.scaricati} allegati letti nell’ultimo giro${v.allegati.da_vedere ? `, ${v.allegati.da_vedere} schede ancora da aprire: si continua ogni ora` : ''})`
                          : ''
                      }`,
                  )
                  .join(', ')}`}
            </p>
          )}
          {!operations && s.connection.last_error && (
            <p className={s.connection.status === 'sospeso' ? 'text-amber-800' : 'text-red-700'}>{s.connection.last_error}</p>
          )}
          {!operations && s.connection.kind !== 'api' && (
            <p className="mt-1 text-xs text-ink/55">
              Collegato dal sito con le vostre credenziali (
              {s.connection.kind === 'pagine' ? 'lettura delle pagine' : 'chiamate interne del sito'}), riletto{' '}
              {s.connection.sync_hours === 1 ? 'ogni ora' : s.connection.sync_hours === 24 ? 'una volta al giorno' : `ogni ${s.connection.sync_hours} ore`}.
            </p>
          )}
          {isManager && s.connection.kind !== 'api' && !s.exploration &&
            (s.connection.remap || s.connection.status !== 'attivo' ? (
              <Button className="mt-2" variant="ghost" disabled={remap.isPending} onClick={() => remap.mutate()}>
                {remap.isPending ? 'Avvio…' : 'Rifai la mappatura del sito'}
              </Button>
            ) : (
              // Anche quando funziona: mancano dati o il sito è cresciuto. Si esplora di nuovo con le credenziali salvate.
              <button className="mt-1 block text-xs text-accent hover:underline" disabled={remap.isPending} onClick={() => remap.mutate()}>
                Mancano dei dati? Rifai la mappatura del sito
              </button>
            ))}
          {isManager && s.connection.kind === 'api' && <ConnectionFields s={s} />}
          {s.exploration && <ExplorationPanel s={s} onDone={refresh} />}
          {isManager && s.connection.status === 'attivo' && <WebWrites s={s} />}
          {isManager && s.connection.status === 'attivo' && <ProgramMappings s={s} />}
          <p className="mt-1 text-xs text-ink/55">
            I dati sono nell’archivio: si possono usare per{' '}
            <button
              className="text-accent hover:underline"
              onClick={() => navigate(`/t/${tenant.id}/persone/importa`)}
            >
              persone e ruoli
            </button>
            ,{' '}
            <button
              className="text-accent hover:underline"
              onClick={() => navigate(`/t/${tenant.id}/controlli`)}
            >
              controlli
            </button>{' '}
            e{' '}
            <button
              className="text-accent hover:underline"
              onClick={() => navigate(`/t/${tenant.id}/indicatori`)}
            >
              indicatori
            </button>
            .
          </p>
          {isManager && (
            <div className="mt-2 flex gap-2">
              <Button
                variant="ghost"
                disabled={sync.isPending || s.connection.syncing}
                onClick={() => sync.mutate()}
              >
                Sincronizza ora
              </Button>
              <Button
                variant="ghost"
                onClick={() =>
                  confirm(`Scollegare ${s.name}? I dati già copiati restano nell’archivio.`) &&
                  disconnect.mutate()
                }
              >
                Scollega
              </Button>
            </div>
          )}
        </div>
      )}
      {isManager && !s.connection && a?.has_template && (
        <div className="mt-3">
          {!connecting ? (
            <Button onClick={() => setConnecting(true)}>Collega {s.name}</Button>
          ) : (
            <ConnectForm
              s={s}
              onDone={() => {
                setConnecting(false);
                refresh();
              }}
            />
          )}
        </div>
      )}
      {isManager && !s.connection && webOffered(s) && (
        <div className="mt-3">
          {s.exploration || webConnecting ? (
            <WebConnectForm
              s={s}
              onDone={() => {
                setWebConnecting(false);
                refresh();
              }}
            />
          ) : a?.has_template ? (
            <button className="text-xs text-accent hover:underline" onClick={() => setWebConnecting(true)}>
              Le API non bastano? Collega {s.name} dal sito con le credenziali
            </button>
          ) : a?.web.status !== 'possibile' ? (
            <button className="text-xs text-accent hover:underline" onClick={() => setWebConnecting(true)}>
              Usate {s.name} da un sito con utente e password? Indicate il sito e collegatelo da lì
            </button>
          ) : (
            <Button onClick={() => setWebConnecting(true)}>Collega {s.name} dal sito</Button>
          )}
        </div>
      )}
      {isManager && !s.connection && (
        <button
          className="mt-3 block text-xs text-ink/45 hover:text-red-700"
          onClick={() =>
            confirm(`Togliere ${s.name} dall’elenco dei programmi usati?`) && remove.mutate()
          }
        >
          Non lo usiamo: togli dall’elenco
        </button>
      )}
      <ErrorText>
        {assess.error
          ? errorMessage(assess.error)
          : sync.error
            ? errorMessage(sync.error)
            : remove.error
              ? errorMessage(remove.error)
              : remap.error
                ? errorMessage(remap.error)
                : ''}
      </ErrorText>
    </Card>
  );
}

type Tone = 'gray' | 'amber' | 'green' | 'red' | 'blue';

/** Stato di un software nell'elenco e nella scheda (ADR 0121): collegato o no, e perché. */
export function softwareState(s: SoftwareDto): { label: string; tone: Tone } {
  const c = s.connection;
  if (c)
    return c.status === 'attivo'
      ? { label: c.kind === 'api' ? 'collegato' : 'collegato dal sito', tone: 'green' }
      : c.status === 'sospeso'
        ? { label: 'sospeso', tone: 'amber' }
        : { label: 'in errore', tone: 'red' };
  if (s.assessing) return { label: 'verifica in corso', tone: 'blue' };
  if (!s.assessment) return s.product_unrecognized ? { label: 'prodotto da indicare', tone: 'amber' } : { label: 'da verificare', tone: 'gray' };
  return { label: 'non collegato', tone: 'gray' };
}

/** Come si collega: dal collegamento fatto o, se manca, dalla valutazione. */
export function softwareMode(s: SoftwareDto): string | null {
  if (s.connection) return s.connection.kind === 'api' ? 'API' : 'dal sito';
  return s.assessment ? FEASIBILITY[s.assessment.feasibility]?.label ?? null : null;
}

export const softwareCategory = (s: SoftwareDto): string | null => s.assessment?.category ?? s.category;

export type LastResult = Record<string, { righe: number; incompleto?: boolean; allegati?: { scaricati: number; da_vedere: number } }>;

/** Errori e avvisi del collegamento e della valutazione: rosso per ciò che non funziona, ambra per ciò che va guardato. */
export function softwareProblems(s: SoftwareDto): Array<{ text: string; tone: 'red' | 'amber' }> {
  const out: Array<{ text: string; tone: 'red' | 'amber' }> = [];
  const c = s.connection;
  const a = s.assessment;
  if (c?.last_error) out.push({ text: c.last_error, tone: c.status === 'sospeso' ? 'amber' : 'red' });
  if (c?.remap) out.push({ text: 'Il sito è cambiato: va rifatta la mappatura.', tone: 'amber' });
  if (c?.progress?.errors)
    out.push({ text: `${c.progress.errors} elementi in errore nella lettura in corso.`, tone: 'red' });
  if (c?.progress?.paused) out.push({ text: `Lettura ferma: ${c.progress.paused}`, tone: 'amber' });
  for (const [k, v] of Object.entries((c?.last_result ?? {}) as LastResult))
    if (v.incompleto) out.push({ text: `Lettura di ${k} fermata al limite di sicurezza.`, tone: 'amber' });
  if (c?.fields_choice && !c.fields_choice.ok) out.push({ text: c.fields_choice.note, tone: 'amber' });
  if (s.exploration && (s.exploration.status === 'errore' || s.exploration.status === 'non_possibile'))
    out.push({
      text: s.exploration.error ?? 'L’esplorazione del sito non è riuscita.',
      tone: s.exploration.status === 'errore' ? 'red' : 'amber',
    });
  if (a && !c) {
    if (a.feasibility === 'collegabile' && !a.has_template && !s.assessing)
      out.push({
        text: 'Ha delle API, ma dalla documentazione il sistema non è riuscito a ricavare un modo sicuro di collegarsi.',
        tone: 'amber',
      });
    if (!a.has_template && WEB_BLOCKED[a.web.status]) out.push({ text: WEB_BLOCKED[a.web.status]!, tone: 'amber' });
  }
  if (a?.doc_source === 'memoria')
    out.push({
      text: 'Non è stato possibile consultare la documentazione: valutazione dalla conoscenza generale, da verificare.',
      tone: 'amber',
    });
  return out;
}

/** «2 errori, 1 avviso»: errori quelli in rosso, avvisi quelli da guardare. */
export function problemsLabel(problems: Array<{ tone: 'red' | 'amber' }>): string {
  const red = problems.filter((p) => p.tone === 'red').length;
  const amber = problems.length - red;
  return [red && `${red} ${red === 1 ? 'errore' : 'errori'}`, amber && `${amber} ${amber === 1 ? 'avviso' : 'avvisi'}`]
    .filter(Boolean)
    .join(', ');
}

const fold = (v: unknown) =>
  String(v ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();

/**
 * Software dell'azienda: li scopre il sistema da come lavorate (conversazioni, file caricati) o li
 * aggiungete voi; per ognuno verifica se e come si può collegare. Nulla è obbligatorio: a mano funziona tutto.
 * Elenco filtrabile come quelli dei dati; la riga apre la scheda del software (ADR 0121).
 */
export function SoftwarePage({
  outcome,
  mail = false,
}: {
  outcome?: { ok: boolean; message: string };
  mail?: boolean;
}) {
  const { tenant, t, isManager } = useTenant();
  const queryClient = useQueryClient();
  // Mentre una verifica o una sincronizzazione è in corso la pagina si aggiorna da sola.
  const list = useSoftwareList();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [adding, setAdding] = useState(false);
  const [cols, setCols] = useState<Record<string, string>>({});
  const started = useRef(new Set<string>());
  const add = useMutation({
    mutationFn: () =>
      api<SoftwareDto>(t('/software'), {
        body: {
          name,
          how: 'dichiarato',
          ...(description.trim() ? { description: description.trim() } : {}),
        },
      }),
    onSuccess: () => {
      setName('');
      setDescription('');
      setAdding(false);
      void queryClient.invalidateQueries({ queryKey: ['tenant', tenant.id, 'software'] });
    },
  });

  // I software scoperti e non ancora valutati (o valutati con regole vecchie) si verificano appena si apre la pagina.
  useEffect(() => {
    if (!isManager) return;
    const pending =
      list.data?.data.filter(
        (s) =>
          ((!s.assessment && !s.product_unrecognized) || s.assessment?.outdated) &&
          !s.assessing &&
          !s.connection &&
          !started.current.has(s.id),
      ) ?? [];
    for (const s of pending) {
      started.current.add(s.id);
      void api(t(`/software/${s.id}/assess`), { body: {} }).then(
        () => queryClient.invalidateQueries({ queryKey: ['tenant', tenant.id, 'software'] }),
        () => undefined,
      );
    }
  }, [list.data]); // eslint-disable-line react-hooks/exhaustive-deps

  const all = list.data?.data ?? [];
  // Le scelte dei filtri vengono dai programmi dell'azienda, non da un elenco fisso.
  const options = (f: (s: SoftwareDto) => string | null) =>
    [...new Set(all.map(f).filter((v): v is string => !!v))].sort((x, y) => x.localeCompare(y, 'it'));
  const categories = options(softwareCategory);
  const states = options((s) => softwareState(s).label);
  const modes = options(softwareMode);
  const shown = all
    .filter(
      (s) =>
        (!cols.nome || fold(`${s.name} ${s.product ?? ''}`).includes(fold(cols.nome))) &&
        (!cols.tipologia || softwareCategory(s) === cols.tipologia) &&
        (!cols.stato || softwareState(s).label === cols.stato) &&
        (!cols.modo || softwareMode(s) === cols.modo) &&
        (!cols.errori || (cols.errori === 'si') === softwareProblems(s).length > 0),
    )
    .sort((x, y) => x.name.localeCompare(y.name, 'it'));
  const open = (s: SoftwareDto) => navigate(`/t/${tenant.id}/software/scheda/${s.id}`);
  const choice = (key: string, values: string[], none: string) => (
    <select value={cols[key] ?? ''} onChange={(e) => setCols({ ...cols, [key]: e.target.value })}>
      <option value="">{none}</option>
      {values.map((v) => (
        <option key={v} value={v}>
          {v}
        </option>
      ))}
    </select>
  );

  return (
    <>
      <PageTitle
        title="Software"
        subtitle="I programmi che usate e cosa se ne può fare: collegarli, leggerne gli export o continuare a mano."
        actions={isManager && !adding ? <Button onClick={() => setAdding(true)}>Aggiungi un programma</Button> : undefined}
      />
      <ErrorText>{list.error ? errorMessage(list.error) : ''}</ErrorText>
      {outcome && (
        <div
          className={`mb-4 rounded-lg px-4 py-3 text-sm ${outcome.ok ? 'bg-emerald-50 text-emerald-800' : 'bg-red-50 text-red-800'}`}
        >
          {outcome.ok ? '✓ ' : ''}
          {outcome.message}
        </div>
      )}
      <div className="space-y-4">
        {adding && (
          <Card>
            <h2 className="mb-3 text-sm font-semibold">Aggiungi un programma</h2>
            <form
              className="flex flex-wrap items-end gap-3"
              onSubmit={(e) => {
                e.preventDefault();
                add.mutate();
              }}
            >
              <div className="min-w-48 flex-1">
                <Field label="Nome" value={name} onChange={(e) => setName(e.target.value)} required />
              </div>
              <div className="min-w-64 flex-[2]">
                <Field
                  label="A cosa vi serve (facoltativo)"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>
              <Button type="submit" disabled={add.isPending}>
                {add.isPending ? 'Verifica…' : 'Aggiungi e verifica'}
              </Button>
              <Button type="button" variant="ghost" onClick={() => setAdding(false)}>
                Annulla
              </Button>
            </form>
            <ErrorText>{add.error ? errorMessage(add.error) : ''}</ErrorText>
          </Card>
        )}
        {list.isLoading ? (
          <SectionLoader />
        ) : !all.length ? (
          <Empty>
            Nessun software ancora. Il sistema li scopre quando descrivete un processo o caricate
            un export; potete anche aggiungerli qui.
          </Empty>
        ) : (
          <Card>
            <div className="overflow-x-auto">
              <table className="bk-list min-w-max">
                <thead>
                  <tr>
                    <th>Nome</th>
                    <th>Tipologia</th>
                    <th>Stato</th>
                    <th>Collegamento</th>
                    <th>Ultima lettura</th>
                    <th>Errori</th>
                  </tr>
                  <tr>
                    <th>
                      <input placeholder="Cerca…" value={cols.nome ?? ''} onChange={(e) => setCols({ ...cols, nome: e.target.value })} />
                    </th>
                    <th>{choice('tipologia', categories, 'tutte')}</th>
                    <th>{choice('stato', states, 'tutti')}</th>
                    <th>{choice('modo', modes, 'tutti')}</th>
                    <th />
                    <th>
                      <select value={cols.errori ?? ''} onChange={(e) => setCols({ ...cols, errori: e.target.value })}>
                        <option value="">tutti</option>
                        <option value="si">con errori o avvisi</option>
                        <option value="no">senza</option>
                      </select>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((s) => {
                    const state = softwareState(s);
                    const problems = softwareProblems(s);
                    const red = problems.filter((p) => p.tone === 'red').length;
                    return (
                      <tr key={s.id} className="cursor-pointer" onClick={() => open(s)}>
                        <td className="font-medium">
                          <a
                            className="text-accent hover:underline"
                            href={`#/t/${tenant.id}/software/scheda/${s.id}`}
                            onClick={(e) => e.stopPropagation()}
                          >
                            {s.name}
                          </a>
                          {s.product && s.product !== s.name && <span className="font-normal text-ink/55"> · {s.product}</span>}
                        </td>
                        <td>{softwareCategory(s) ?? <span className="text-ink/40">—</span>}</td>
                        <td>
                          <Badge tone={state.tone}>{state.label}</Badge>
                        </td>
                        <td>{softwareMode(s) ?? <span className="text-ink/40">—</span>}</td>
                        <td>
                          {s.connection?.syncing ? (
                            <span className="text-ink/60">in corso…</span>
                          ) : s.connection?.last_sync_at ? (
                            dateTime(s.connection.last_sync_at)
                          ) : (
                            <span className="text-ink/40">—</span>
                          )}
                        </td>
                        <td>
                          {problems.length ? (
                            <span className={red ? 'text-red-700' : 'text-amber-700'} title={problems.map((p) => p.text).join('\n')}>
                              {problemsLabel(problems)}
                            </span>
                          ) : (
                            <span className="text-ink/40">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {!shown.length && <p className="py-3 text-center text-xs text-ink/50">Nessuno con questo filtro.</p>}
            </div>
          </Card>
        )}
        <MailboxesPanel startOpen={mail} />
      </div>
    </>
  );
}

/** Elenco dei software dell'azienda; mentre una verifica o una lettura è in corso si aggiorna da solo. */
export function useSoftwareList() {
  return useTenantQuery<{ data: SoftwareDto[] }>(['software'], '/software', {
    refetchInterval: (d) =>
      d?.data.some((s) => s.assessing || s.connection?.syncing || s.connection?.choosing_fields || s.exploration?.status === 'in_corso') ? 4000 : false,
  });
}
